import { Component, OnInit, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import {
  EmbeddedVideo,
  EmbeddedVideoService,
} from "../../../../core/services/embedded-video.service";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { BaseComponent } from "../../../../core/shared/base.component";
import {
  VideoCardComponent,
  VideoCardConfig,
} from "../../../../core/shared/components/video-card/video-card.component";
import { YouTubePlayerComponent } from "../../../../core/shared/components/youtube-player/youtube-player.component";

@Component({
  selector: "app-videos-config",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    VideoCardComponent,
    YouTubePlayerComponent,
  ],
  templateUrl: "./videos-config.component.html",
  styleUrls: ["./videos-config.component.scss"],
})
export class VideosConfigComponent extends BaseComponent implements OnInit {
  private videoService = inject(EmbeddedVideoService);
  private sanitizer = inject(DomSanitizer);

  videos: EmbeddedVideo[] = [];
  loading = false;
  errorMessage = "";
  successMessage = "";

  // Estados de carga individuales para cada video
  loadingStates: { [key: string]: boolean } = {};

  // Propiedades para el visualizador de videos
  mostrarVisualizadorVideo = false;
  videoUrlActual = "";
  videoIdActual = "";
  safeVideoUrlActual: SafeResourceUrl | null = null;

  // Configuración de videos disponibles
  videoConfigs: VideoCardConfig[] = [
    {
      key: "video_inicio",
      displayName: "Video Inicio",
      placeholder: "Ingresa la URL del video de inicio",
    },
    {
      key: "video_modal_analisis_del_presente",
      displayName: "Video Modal Análisis del Presente",
      placeholder: "Ingresa la URL del video de análisis del presente",
    },
    {
      key: "video_modal_resultado_de_autodiagnostico",
      displayName: "Video Modal Resultado de Autodiagnóstico",
      placeholder: "Ingresa la URL del video de resultado de autodiagnóstico",
    },
    {
      key: "video_modal_Prueba_de_autoconocimiento",
      displayName: "Video Modal Círculo de Vida",
      placeholder: "Ingresa la URL del video del círculo de vida",
    },
    {
      key: "video_modal_visionboard",
      displayName: "Video Modal Visionboard",
      placeholder: "Ingresa la URL del video de visionboard",
    },
    {
      key: "video_modal_prd",
      displayName: "Video Modal PRD",
      placeholder: "Ingresa la URL del video de PRD",
    },
    {
      key: "video_modal_objectives",
      displayName: "Video Modal Objetivos",
      placeholder: "Ingresa la URL del video de objetivos",
    },
  ];

  // Propiedad para almacenar las URLs de los videos para edición directa
  videoUrls: { [key: string]: string } = {};

  ngOnInit(): void {
    this.initializeVideoUrls();
    this.initializeLoadingStates();
    this.loadVideos();
  }

  private initializeVideoUrls(): void {
    this.videoConfigs.forEach((config) => {
      this.videoUrls[config.key] = "";
    });
  }

  private initializeLoadingStates(): void {
    this.videoConfigs.forEach((config) => {
      this.loadingStates[config.key] = false;
    });
  }

  // Método para obtener el estado de carga de un video específico
  isVideoLoading(videoKey: string): boolean {
    return this.loadingStates[videoKey] || false;
  }

  loadVideos(): void {
    this.loading = true;
    this.videoService.getVideos().subscribe({
      next: (videos) => {
        this.videos = videos;
        // Inicializar las URLs en la nueva propiedad
        videos.forEach((video) => {
          if (video.type && video.url) {
            this.videoUrls[video.type] = video.url;
          }
        });
        this.loading = false;
      },
      error: (error) => {
        console.error("Error loading videos:", error);
        this.errorMessage = "Error al cargar los videos.";
        this.loading = false;
      },
    });
  }

  // Método para actualizar video directamente
  actualizarVideoDirectamente(type: string): void {
    if (!this.videoUrls[type]) {
      this.errorMessage = "Por favor ingresa una URL de video válida.";
      return;
    }

    // Activar loading solo para este video específico
    this.loadingStates[type] = true;
    this.errorMessage = "";
    this.successMessage = "";

    // Crear objeto de video para actualizar
    const video: EmbeddedVideo = {
      type: type,
      url: this.videoUrls[type],
    };

    // Verificar si el video ya existe
    const existingVideo = this.videos.find((v) => v.type === type);

    if (existingVideo) {
      // Actualizar video existente
      this.videoService.updateVideo(video).subscribe({
        next: (response) => {
          this.success(
            response.data.message || "Video actualizado correctamente."
          );
          this.loadVideos();
          this.loadingStates[type] = false;
        },
        error: (error) => {
          console.error("Error updating video:", error);
          this.handleVideoError(error);
          this.loadingStates[type] = false;
        },
      });
    } else {
      // Crear nuevo video
      this.videoService.createVideo(video).subscribe({
        next: (response) => {
          this.successMessage =
            response.data.message || "Video creado correctamente.";
          this.loadVideos();
          this.loadingStates[type] = false;
        },
        error: (error) => {
          console.error("Error creating video:", error);
          this.handleVideoError(error);
          this.loadingStates[type] = false;
        },
      });
    }
  }

  private handleVideoError(error: any): void {
    if (error?.error?.message) {
      this.errorMessage = error.error.message;
    } else if (error?.error?.data?.message) {
      this.errorMessage = error.error.data.message;
    } else {
      this.errorMessage =
        "Error al procesar el video. La URL debe ser una dirección válida de YouTube.";
    }
  }

  getYoutubeEmbedUrl(url: string): SafeResourceUrl {
    let videoId = this.extractVideoId(url);
    if (videoId) {
      return this.sanitizer.bypassSecurityTrustResourceUrl(
        `https://www.youtube.com/embed/${videoId}`
      );
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl("");
  }

  extractVideoId(url: string): string | null {
    return YouTubePlayerComponent.extractVideoId(url);
  }

  visualizarVideo(type: string): void {
    // Verificar si hay una URL en la propiedad videoUrls
    let urlParaVisualizar: string | undefined = this.videoUrls[type];

    if (!urlParaVisualizar) {
      // Si no, buscar en videos
      const video = this.videos.find((v) => v.type === type);
      if (video && video.url) {
        urlParaVisualizar = video.url;
      }
    }

    if (urlParaVisualizar) {
      this.videoUrlActual = urlParaVisualizar;
      this.videoIdActual = this.extractVideoId(urlParaVisualizar) || "";
      this.safeVideoUrlActual = this.getYoutubeEmbedUrl(urlParaVisualizar);
      this.mostrarVisualizadorVideo = true;
    } else {
      this.errorMessage = "No hay URL de video disponible para visualizar.";
      this.videoUrlActual = "";
      this.videoIdActual = "";
      this.safeVideoUrlActual = null;
      this.mostrarVisualizadorVideo = false;
    }
  }

  cerrarVisualizador(): void {
    this.mostrarVisualizadorVideo = false;
    this.videoUrlActual = "";
    this.videoIdActual = "";
    this.safeVideoUrlActual = null;
  }

  // Métodos para manejar eventos del componente hijo
  onVideoUrlChange(videoKey: string, newUrl: string): void {
    this.videoUrls[videoKey] = newUrl;
  }
}
